import {
  completePermissions,
  heldBy,
  PAGE_CATALOG,
  pageOfTab,
  pagesOpenedBy,
  tabsOpenedBy,
  type PageDefinition,
  type TabDefinition,
} from '@obraplan/core/domain/access/pages';
import {
  PERMISSION_CATALOG,
  type KnownPermissionKey,
} from '@obraplan/core/domain/access/permissions';

/**
 * As contas do editor de papéis, fora do componente.
 *
 * O conjunto marcado está sempre **fechado** — com tudo o que as telas e ações marcadas
 * trazem junto (`completePermissions`). É a mesma conta que o caso de uso faz ao gravar,
 * então o que a tela mostra marcado é exatamente o que o papel vai ter.
 */

export type Selection = ReadonlySet<KnownPermissionKey>;

const DESCRIPTION = new Map(
  PERMISSION_CATALOG.map((definition) => [definition.key, definition.description]),
);

/** Como a permissão se chama na tela: a tela e a aba pelo nome, a ação pela descrição. */
export function permissionLabel(key: KnownPermissionKey): string {
  const page = PAGE_CATALOG.find((candidate) => candidate.permission === key);
  if (page !== undefined) return `Tela ${page.label}`;
  const tab = pageOfTab(key)?.tabs?.find((candidate) => candidate.permission === key);
  if (tab !== undefined) return `Aba ${tab.label}`;
  return DESCRIPTION.get(key) ?? key;
}

/** Marcar a tela marca todas as abas dela (RN-627): quem quer esconder uma, desmarca. */
export function selectPage(selection: Selection, page: PageDefinition): Set<KnownPermissionKey> {
  return completePermissions([
    ...selection,
    page.permission,
    ...(page.tabs ?? []).map((tab) => tab.permission),
  ]);
}

/**
 * Desmarcar a tela leva junto as abas dela e as ações que só faziam sentido nela.
 *
 * "Planejar mão de obra" também mora na Programação do dia: se ela continua marcada, a
 * ação fica. O que a tela lia **não** sai — a leitura pode servir a outra tela, e tirá-la
 * é decisão de quem edita, na lista completa de permissões.
 */
export function unselectPage(selection: Selection, page: PageDefinition): Set<KnownPermissionKey> {
  const next = new Set(selection);
  next.delete(page.permission);
  for (const tab of page.tabs ?? []) next.delete(tab.permission);

  const stillOpen = PAGE_CATALOG.filter((other) => next.has(other.permission));
  for (const action of page.actions) {
    const usedElsewhere = stillOpen.some((other) => other.actions.includes(action));
    if (!usedElsewhere && heldBy(action, next).length === 0) next.delete(action);
  }
  return next;
}

/** A aba marcada traz a tela, se ela ainda não estava (RN-627). */
export function selectTab(selection: Selection, tab: TabDefinition): Set<KnownPermissionKey> {
  return completePermissions([...selection, tab.permission]);
}

/**
 * Desmarcar a última aba desmarca a tela (RN-628): uma tela de abas sem nenhuma abriria
 * vazia, e é mais honesto o editor dizer isso tirando a tela do que deixá-la marcada à toa.
 */
export function unselectTab(
  selection: Selection,
  page: PageDefinition,
  tab: TabDefinition,
): Set<KnownPermissionKey> {
  const next = new Set(selection);
  next.delete(tab.permission);
  return tabsOpenedBy(page, next).length === 0 ? unselectPage(next, page) : next;
}

export function selectPermission(
  selection: Selection,
  key: KnownPermissionKey,
): Set<KnownPermissionKey> {
  return completePermissions([...selection, key]);
}

/** Só sai o que nada marcado exige — o resto a tela nem deixa desmarcar. */
export function unselectPermission(
  selection: Selection,
  key: KnownPermissionKey,
): Set<KnownPermissionKey> {
  if (heldBy(key, selection).length > 0) return new Set(selection);
  const next = new Set(selection);
  next.delete(key);
  return next;
}

/**
 * Quem trava esta permissão, já no idioma da tela. Vazio = escolha livre.
 *
 * As telas vêm antes das ações: "a tela Planejamento lê isto" é o motivo que a pessoa
 * reconhece, e é o que ela desmarcaria para soltar a caixa. A aba não entra quando a tela
 * dela já está na lista — ela trava pela tela, e repetir as quatro abas da Mão de obra só
 * esconderia o motivo.
 */
export function lockedBy(selection: Selection, key: KnownPermissionKey): string[] {
  const holders = heldBy(key, selection);
  const pages = PAGE_CATALOG.filter((page) => holders.includes(page.permission)).map(
    (page) => page.permission,
  );
  const others = holders.filter((holder) => {
    if (pages.includes(holder)) return false;
    const owner = pageOfTab(holder);
    return owner === undefined || !pages.includes(owner.permission);
  });
  return [...pages, ...others].map(permissionLabel);
}

export function pagesOf(selection: Selection): PageDefinition[] {
  return pagesOpenedBy(selection);
}
